import { Option } from '@sapphire/result';
import { Nullable } from '@ps/utils';
import {
  DEFAULT_LAUNCH_SAME_SETTINGS,
  FilterAudienceForm,
  INITIAL_RESPONDENT_LIST_UPLOAD_STATE,
  RespondentListUploadState,
  Survey,
  sharesLaunchSettings,
  SurveyLaunchFormMapper,
  SurveyClassificationKind,
  SurveyLaunchFormState,
  withSharedLaunchSettings,
} from '@ps/survey-utils';
import {
  DEFAULT_CHILD_LAUNCH_STATE,
  DEFAULT_SURVEY_CHILD_KEY,
} from '../../survey-creation-state.constants';
import { SurveyCreationLaunchState } from '../../survey-creation-state.model';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import { SurveyCreationCoreContext } from '../../../utils/core-context/survey-creation-action-core-context.helper';
import {
  ChildMapPatch,
  ChildScopeContext,
  patchChildrenMap,
  resolveActiveChildKey as resolveActiveChildKeyOf,
  SurveyChildScope,
} from '../../survey-creation-child-registry.helper';

/**
 * A fresh launch state targeting `rootSurveyId` — mirrors
 * `initializeCoreState`: the one child is keyed by the root id directly in
 * edit mode, else the empty-string pre-draft placeholder.
 */
export function initializeLaunchState(
  rootSurveyId: Nullable<number>,
): SurveyCreationLaunchState {
  const childKey = rootSurveyId ?? DEFAULT_SURVEY_CHILD_KEY;

  return {
    children: {
      [childKey]: DEFAULT_CHILD_LAUNCH_STATE,
    },
    pricing: null,
    isPricingLoading: false,
    isLaunching: false,
    aidaEligibility: null,
    respondentListUploads: {
      [childKey]: { ...INITIAL_RESPONDENT_LIST_UPLOAD_STATE },
    },
    childrenPricing: null,
    draftSurveyIdsToLaunch: null,
    isChildrenPricingLoading: false,
    isLaunchingAll: false,
    sameSettings: DEFAULT_LAUNCH_SAME_SETTINGS,
  };
}

/** Mirrors `isLoadedCoreSibling`: a locale switch re-`init`s with a sibling's id. */
export function isLoadedLaunchSibling(
  children: Record<SurveyChildKey, SurveyLaunchFormState>,
  surveyId: number,
): boolean {
  return !!children[surveyId] && Object.keys(children).length > 1;
}

/** Registers a default launch child per sibling not yet known, keeping existing ones. */
export function registerLaunchChildren(
  state: SurveyCreationLaunchState,
  surveyIds: number[],
): SurveyCreationLaunchState {
  const newIds = surveyIds.filter((surveyId) => !state.children[surveyId]);

  return {
    ...state,
    children: {
      ...state.children,
      ...Object.fromEntries(
        newIds.map((surveyId) => [surveyId, DEFAULT_CHILD_LAUNCH_STATE]),
      ),
    },
    respondentListUploads: {
      ...state.respondentListUploads,
      ...Object.fromEntries(
        newIds.map((surveyId) => [
          surveyId,
          { ...INITIAL_RESPONDENT_LIST_UPLOAD_STATE },
        ]),
      ),
    },
  };
}

export function resolveActiveChildKey(
  state: SurveyCreationLaunchState,
  activeChildKey: Nullable<SurveyChildKey>,
): SurveyChildKey {
  return resolveActiveChildKeyOf(
    state.children,
    activeChildKey,
    DEFAULT_SURVEY_CHILD_KEY,
  );
}

/**
 * Copies the edited child's sections whose "same …" box is ticked onto every
 * other child the launch page holds, project row included — for a kind that
 * shares launch settings on one project PUT. Unticked sections are left as
 * each child holds them, so unticking restores nothing.
 */
export function withSharedLaunchSettingsAcrossChildren(
  state: SurveyCreationLaunchState,
  context: SurveyCreationCoreContext,
): SurveyCreationLaunchState {
  const activeKey = resolveActiveChildKey(state, context.activeChildKey);
  return sharingKindOf(context)
    .zip(Option.from(state.children[activeKey]))
    .mapOr(state, ([sharingKind, parent]) => ({
      ...state,
      children: Object.fromEntries(
        Object.entries(state.children).map(([key, child]) => [
          key,
          key === String(activeKey)
            ? child
            : {
                ...child,
                value: withSharedLaunchSettings(
                  child.value,
                  parent.value,
                  state.sameSettings,
                  sharingKind,
                ),
              },
        ]),
      ),
    }));
}

/** Whether the action's survey kind shares launch settings across its children. */
export function sharesLaunchSettingsIn(
  context: SurveyCreationCoreContext,
): boolean {
  return sharingKindOf(context).isSome();
}

function sharingKindOf({
  kind,
}: SurveyCreationCoreContext): Option<SurveyClassificationKind> {
  return Option.from(kind)
    .map((classification) => classification.kind)
    .filter(sharesLaunchSettings);
}

export function patchChildFilterAudience(
  child: SurveyLaunchFormState,
  filterAudience: Partial<FilterAudienceForm>,
): SurveyLaunchFormState {
  return {
    value: {
      ...child.value,
      filterAudience: { ...child.value.filterAudience, ...filterAudience },
    },
    valid: child.valid,
    errors: child.errors,
    isDirty: child.isDirty,
  };
}

export function buildLoadedLaunchChild(survey: Survey): SurveyLaunchFormState {
  return {
    value: new SurveyLaunchFormMapper().fromSurvey(survey),
    valid: true,
    errors: [],
    isDirty: false,
  };
}

export function keyedLoadedLaunchChildren(
  surveys: readonly Survey[],
): Record<number, SurveyLaunchFormState> {
  return Object.fromEntries(
    surveys.map((survey) => [survey.id, buildLoadedLaunchChild(survey)]),
  );
}

export type ChildPatch = ChildMapPatch<SurveyLaunchFormState>;

export function patchChildren(
  state: SurveyCreationLaunchState,
  context: ChildScopeContext,
  scope: SurveyChildScope,
  patch: ChildPatch,
): SurveyCreationLaunchState {
  return {
    ...state,
    children: patchChildrenMap(
      state.children,
      context,
      scope,
      patch,
      DEFAULT_CHILD_LAUNCH_STATE,
    ),
  };
}

export type RespondentListUploadPatch =
  ChildMapPatch<RespondentListUploadState>;

export function patchRespondentListUploads(
  state: SurveyCreationLaunchState,
  context: ChildScopeContext,
  scope: SurveyChildScope,
  patch: RespondentListUploadPatch,
): SurveyCreationLaunchState {
  return {
    ...state,
    respondentListUploads: patchChildrenMap(
      state.respondentListUploads,
      context,
      scope,
      patch,
      INITIAL_RESPONDENT_LIST_UPLOAD_STATE,
    ),
  };
}
